import { useCallback, useEffect, useState } from "react";
import { CSSProperties } from "styled-components";
import * as d3 from "d3";
import * as d3Zoom from "d3-zoom";
import { Size } from "@hokuto/jam-core";
import { ZoomControlsSchema } from "../types.js";
import { getCanvasContext, getSafeRect } from "../utils/canvas.js";
import { ZoomControls } from "../utils/zoom/controls.js";
import { CanvasDrawer } from "../utils/drawer.js";
import { debounce } from "../utils/debounce.js";
import { ANIMATION_DURATION } from "../const.js";
import { getChildrenParams } from "./canvas.js";
import { ParamsObserver } from "../utils/observers/params.js";
import { Observable } from "../utils/observable.js";

export function drawCanvasFromBackground(args: {
  canvas: HTMLCanvasElement;
  canvasDrawer: CanvasDrawer;
  transformation: d3Zoom.ZoomTransform;
}) {
  const { canvas, canvasDrawer, transformation } = args;
  const { canvas: backgroundCanvas, currentStatus: canvasStatus } =
    canvasDrawer;
  if (!canvasStatus) return;
  const context = getCanvasContext({ canvas });
  const canvasScale = 1 / canvasStatus.scale;
  const scaleDiff = canvasScale / transformation.k;

  const sx =
    (-canvasStatus.x * canvasScale - transformation.x * scaleDiff) *
    devicePixelRatio;
  const sy =
    (-canvasStatus.y * canvasScale - transformation.y * scaleDiff) *
    devicePixelRatio;

  const sw = canvas.width * scaleDiff * devicePixelRatio;
  const sh = canvas.height * scaleDiff * devicePixelRatio;
  const dx = 0;
  const dy = 0;
  const dw = canvas.width;
  const dh = canvas.height;

  const safeRect = getSafeRect(
    backgroundCanvas,
    sx,
    sy,
    sw,
    sh,
    dx,
    dy,
    dw,
    dh
  );
  context.drawImage(...safeRect);
}

export function clearCanvas(args: {
  canvas: HTMLCanvasElement;
  bgColor: string;
}) {
  const { canvas, bgColor } = args;
  const context = getCanvasContext({ canvas });
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = bgColor;
  context.fillRect(0, 0, canvas.width, canvas.height);
}

export const defaultInit = new d3Zoom.ZoomTransform(1, 0, 0);

export function useControls(
  args: Size & {
    bgColor: string;
    setChildrenParams: (params: ReturnType<typeof getChildrenParams>) => void;
    canvas?: HTMLCanvasElement;
    duration?: number;
    minZoom?: number;
    maxZoom?: number;
    grabCursor?: CSSProperties["cursor"];
    paramsObserver?: ParamsObserver;
    init?: d3Zoom.ZoomTransform;
  }
) {
  const {
    canvas,
    width,
    height,
    minZoom,
    maxZoom,
    bgColor,
    setChildrenParams,
    duration = ANIMATION_DURATION,
    grabCursor = "grab",
    init = defaultInit,
    paramsObserver,
  } = args;

  const [initialTransform] = useState(init);
  const [controls, setControls] =
    useState<ZoomControlsSchema<HTMLCanvasElement>>();
  const [transformation, setTransformation] = useState<d3Zoom.ZoomTransform>();
  const [drawersObservable] = useState(
    new Observable<{ drawers: CanvasDrawer[] }>({ drawers: [] })
  );

  const updateControls = useCallback(
    (current?: ZoomControlsSchema<HTMLCanvasElement>) => {
      setControls((previous) => {
        const value = previous?.observable.get();
        value && current?.observable.set(value);
        paramsObserver?.setControls(current);
        return current;
      });
    },
    [paramsObserver]
  );

  const transformCallback = useCallback(
    debounce((transformation: d3Zoom.ZoomTransform) => {
      if (!controls) return;
      const childrenParams = getChildrenParams({
        controls,
        transformation,
      });
      setChildrenParams(childrenParams);
      setTransformation(transformation);
    }, duration),
    [duration, controls]
  );

  const drawCanvas = useCallback(
    (args: {
      transformation: d3.ZoomTransform;
      canvas: HTMLCanvasElement;
      cancelAnimations?: boolean;
    }) => {
      const { drawers } = drawersObservable.get();
      const { transformation, canvas, cancelAnimations } = args;
      clearCanvas({ canvas, bgColor });
      for (const canvasDrawer of drawers) {
        if (!canvasDrawer) continue;
        cancelAnimations && canvasDrawer.cancelAnimations();
        drawCanvasFromBackground({
          canvas,
          canvasDrawer,
          transformation,
        });
      }
    },
    [bgColor]
  );

  const moveTransformCallback = useCallback(
    (
      controls: ZoomControlsSchema<HTMLCanvasElement>,
      transformation: d3Zoom.ZoomTransform
    ) => {
      requestAnimationFrame(() => {
        drawCanvas({
          canvas: controls.element,
          cancelAnimations: true,
          transformation,
        });
        transformCallback(transformation);
      });
    },
    [transformCallback, drawCanvas]
  );

  useEffect(() => {
    if (!canvas) return updateControls(undefined);
    const initialSize = { width, height };

    const controls = ZoomControls.create({
      element: canvas,
      initialSize,
      duration,
      minZoom,
      maxZoom,
    });

    if (!transformation) {
      const d3Element = d3.select(controls.element);
      d3Element.call(
        controls.zoom.transform,
        d3.zoomIdentity
          .translate(initialTransform.x, initialTransform.y)
          .scale(initialTransform.k)
      );
      setTransformation(initialTransform);
    }
    updateControls(controls);
    return () => {
      controls.observable.destroy();
    };
  }, [canvas, duration, minZoom, maxZoom]);

  useEffect(() => {
    if (!controls) return;
    const d3Element = d3.select(controls.element);
    const listener = controls.observable.subscribe(({ action, current }) => {
      const cursor =
        action === "zoom"
          ? "move"
          : action === "start"
          ? "grabbing"
          : grabCursor;

      d3Element.style("cursor", cursor);
      action === "zoom" && current && moveTransformCallback(controls, current);
    });

    return () => {
      listener();
    };
  }, [controls, grabCursor, moveTransformCallback]);

  return { controls, drawersObservable, transformation };
}
